/**
 * Member-facing wastewater overview, relevance, and equity copy.
 */

import type { WastewaterActivitySnapshot } from '../../services/wastewaterInventoryService';

export function formatMtCo2e(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function formatAcreFeet(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function formatTreatmentLevel(level: string | null | undefined): string {
  const raw = (level ?? '').trim();
  if (!raw) return '—';
  return raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Process emissions per resident, in kilograms CO₂e. */
export function formatKgPerResident(mtCo2e: number, population: number): string {
  if (!(population > 0) || !Number.isFinite(mtCo2e)) return '—';
  const kg = (mtCo2e * 1000) / population;
  const digits = kg < 1 ? 2 : 1;
  return kg.toLocaleString(undefined, { maximumFractionDigits: digits });
}

export const WASTEWATER_OVERVIEW_DESCRIPTION =
  'This chart shows process greenhouse gases from municipal wastewater treatment — mainly nitrous oxide from treated effluent. ' +
  'These emissions are a very small share of a typical community inventory. Plant electricity is counted in building energy, not here. ' +
  'The 2020 value is the ClimatePlans wastewater inventory; other years follow the change in service population (residents plus workplace workers).';

function volumePhrase(activity: WastewaterActivitySnapshot | null): string {
  if (!activity?.treatmentVolumeAf) return '';
  const af = formatAcreFeet(activity.treatmentVolumeAf);
  const year = activity.inventoryYear ?? 2020;
  const method = (activity.volumeMethod || '').toLowerCase();
  if (method === 'supplemental' || method === 'metered') {
    return `${af} acre-feet of collection volume in ${year} from the city’s Urban Water Management Plan`;
  }
  if (method === 'estimated' || method === 'scaled' || method === 'default') {
    return `an estimated ${af} acre-feet of treatment volume in ${year}`;
  }
  return `${af} acre-feet of treatment volume in ${year}`;
}

export function buildWastewaterOverviewNarrative(args: {
  location: string;
  latestYear: number | null;
  latestMt: number | null;
  storedYear: number | null;
  storedMt: number | null;
  scaleBasis: string | null;
  activity: WastewaterActivitySnapshot | null;
  population: number | null;
}): string {
  const { location, latestYear, latestMt, storedYear, storedMt, scaleBasis, activity, population } = args;
  if (latestMt == null || latestYear == null || storedMt == null || storedYear == null) {
    return `${location} wastewater data could not be scaled for the latest inventory year.`;
  }

  let text = `${location} wastewater process emissions are about ${formatMtCo2e(latestMt)} metric tons CO₂e in ${latestYear}`;
  if (population != null && population > 0) {
    text += `, or roughly ${formatKgPerResident(latestMt, population)} kilograms CO₂e per resident`;
  }
  text +=
    '. That is a very small contribution to the community inventory — far smaller than buildings, transportation, or landfill waste.';

  const treatment = formatTreatmentLevel(activity?.treatmentLevel);
  const volume = volumePhrase(activity);
  if (activity?.facilityName || volume || (treatment && treatment !== '—')) {
    text += ` The ${storedYear} inventory`;
    if (activity?.facilityName) text += ` is associated with ${activity.facilityName}`;
    if (volume) text += `${activity?.facilityName ? ', using ' : ' uses '}${volume}`;
    if (treatment && treatment !== '—') text += `, with ${treatment.toLowerCase()} treatment`;
    text += '.';
  }

  if (latestYear !== storedYear) {
    if (scaleBasis === 'service_population') {
      text += ` ${latestYear} is scaled from that ${storedYear} inventory by the change in service population (residents plus workplace workers).`;
    } else if (scaleBasis === 'population_placeholder') {
      text += ` ${latestYear} is scaled from that ${storedYear} inventory by resident population because workplace workers were not available.`;
    }
  }

  text +=
    ' On the GHG inventory page, this estimate is added to landfill waste as Waste & wastewater; landfill methane dominates that combined sector.';
  return text;
}

export const WASTEWATER_RELEVANCE_DESCRIPTION =
  'Wastewater process emissions are a very small slice of local climate action. They are typically well under 1% of a community inventory — often closer to a few tenths of a percent or less — because this module counts treatment-process gases only. Plant electricity is already in the buildings inventory. Completeness still matters for protocol inventories, but the large reduction opportunities remain in energy, transportation, and organics.';

export const WASTEWATER_RELEVANCE_CARDS: Array<{ title: string; body: string }> = [
  {
    title: 'A small inventory contribution',
    body:
      'For most California cities, wastewater process N₂O is a rounding-level share of community totals. Prioritize buildings, vehicles, and landfill organics for tons reduced. Keep this sector in the inventory so the community total is complete, not because it is a major mitigation lever.',
  },
  {
    title: 'What local action can still change',
    body:
      'Indoor water conservation and recycled-water use can lower the volume sent to treatment. Upgrading from secondary to tertiary treatment, or improving nitrogen removal, can cut the N₂O emission factor by about 3.5 times. Energy efficiency and electrification at the plant show up in the energy inventory, not in these process totals.',
  },
];

export const WASTEWATER_EQUITY_DESCRIPTION =
  'Even when climate totals are small, wastewater systems are not experienced equally. Plant siting, sewer overflows, septic gaps, and rate increases for treatment upgrades fall unevenly across neighborhoods and across cities that generate wastewater versus cities that host the plant.';

export const WASTEWATER_EQUITY_CARDS: Array<{ title: string; body: string }> = [
  {
    title: 'Facility-adjacent neighborhoods',
    body:
      'Odors, biosolids trucking, flooding, and overflow risk are concentrated near treatment plants and lift stations. Those burdens often sit in communities that did not choose to host a regional facility, even when emissions are attributed to the cities that generate the wastewater.',
  },
  {
    title: 'Who is on the system — and who pays',
    body:
      'Households on septic systems are not in this centralized-treatment inventory, yet they can face different local water-quality and maintenance burdens. Where plants are upgraded for nutrient removal or recycled water, rate design determines whether lower-income customers carry a disproportionate share of capital costs.',
  },
];
